import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineDownload,
    AiOutlineSearch,
    AiOutlineFileAdd,
    AiOutlineCheckCircle,
    AiOutlineFileText,
    AiOutlineCalendar,
    AiOutlineUser,
    AiOutlineLeft,
    AiOutlineRight,
    AiOutlineFolder
} from "react-icons/ai";
import authService from "../../services/authService";

// ----- REAL API -----
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin/documents`;

async function apiGetAmendments(statusFilter = '', yearFilter = '') {
    let url = `${API_BASE_URL}/amendments/?t=${Date.now()}&_cb=${Math.random()}`;
    if (statusFilter && statusFilter !== 'All Statuses') {
        url += `&status=${statusFilter}`;
    }
    if (yearFilter && yearFilter !== 'All Years') {
        url += `&year=${yearFilter}`;
    }
    
    const response = await authService.adminApiCall(url);
    
    if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        if (response.status === 401) {
            console.log('Authentication failed - redirecting to login');
            localStorage.removeItem('adminAuthToken');
            window.location.href = '/admin/login';
            return [];
        }
        
        throw new Error(`Failed to fetch amendments: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data || [];
}

async function apiUploadDraftCopy(userId, year, file, amendmentId = null) {
    const token = localStorage.getItem('adminAuthToken');
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('year', year);
    formData.append('file', file);
    formData.append('type', 'draft');
    // If amendmentId is provided, send it to link the tax return to the amendment
    if (amendmentId) {
        formData.append('amendment_id', amendmentId);
    }

    const response = await fetch(`${API_BASE_URL}/tax-returns/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });

    if (!response.ok) {
        throw new Error('Failed to upload draft copy');
    }

    return await response.json();
}

async function apiUploadFinalCopy(userId, year, file, amendmentId = null) {
    const token = localStorage.getItem('adminAuthToken');
    if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
    }
    
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('year', year);
    formData.append('file', file);
    formData.append('type', 'final');
    // If amendmentId is provided, send it to link the tax return to the amendment
    if (amendmentId) {
        formData.append('amendment_id', amendmentId);
    }

    const response = await fetch(`${API_BASE_URL}/tax-returns/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });

    if (!response.ok) {
        throw new Error('Failed to upload final copy');
    }

    return await response.json();
}

async function apiDownloadDraftCopy(taxReturnId) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/${taxReturnId}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download draft copy');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `draft_copy_${taxReturnId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

async function apiDownloadFinalCopy(taxReturnId) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/${taxReturnId}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download final copy');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `final_copy_${taxReturnId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

async function apiDownloadAmendment({ id, filename }) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/amendments/${id}/download/`);
    
    if (!response.ok) {
        throw new Error('Failed to download amendment');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'amendment';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

async function apiDownloadAmendmentsZip(userId, year) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/amendments/download-zip/?user_id=${userId}&year=${year}`);
    
    if (!response.ok) {
        throw new Error('Failed to download zip file');
    }
    
    // Check if response is a zip file
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/zip')) {
        // Get the filename from Content-Disposition header
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'amendments.zip';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } else {
        // Handle error response
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download zip file');
    }
}

async function apiGetAllTaxReturnStatuses() {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/?t=${Date.now()}&_cb=${Math.random()}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        console.error('Tax Returns API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Tax Returns Error response:', errorText);
        
        if (response.status === 401) {
            console.log('Authentication failed - redirecting to login');
            localStorage.removeItem('adminAuthToken');
            window.location.href = '/admin/login';
            return [];
        }
        
        throw new Error(`Failed to get tax return statuses: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data || [];
}

export default function AmendmentUploads() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedYear, setSelectedYear] = useState("All Years");
    const [selectedStatus, setSelectedStatus] = useState("All Statuses");
    const [successMessage, setSuccessMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    
    // Workflow management state
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [showFinalModal, setShowFinalModal] = useState(false);
    const [draftFile, setDraftFile] = useState(null);
    const [finalFile, setFinalFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [taxReturnStatus, setTaxReturnStatus] = useState({});
    
    // View mode: 'overview' or 'user-detail'
    const [viewMode, setViewMode] = useState('overview');
    const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);
    const [selectedYearForUser, setSelectedYearForUser] = useState("All Years");
    const [selectedUserName, setSelectedUserName] = useState("");
    const [currentYearIndex, setCurrentYearIndex] = useState(0);

    // Format date to CST timezone
    function formatToCST(dateString) {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    }

    // Fetch data on mount and when filters change
    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const [amendmentsData, taxReturnsData] = await Promise.all([
                    apiGetAmendments(selectedStatus, selectedYear),
                    apiGetAllTaxReturnStatuses()
                ]);
                
                // Process amendments data to include draft and final copy information
                const processedData = amendmentsData.map(amendment => {
                    // Get amendment category ('amendment' or 'notices')
                    const amendmentCategory = amendment.category || 'amendment';
                    
                    // Find draft and final copies for this user and year
                    // IMPORTANT: Filter by category to match amendment's category
                    // This ensures we only get tax returns for this specific amendment, not regular uploads
                    const userTaxReturns = taxReturnsData.filter(tr => 
                        tr.user === amendment.user && 
                        tr.year === amendment.year &&
                        tr.category === amendmentCategory  // Match category: 'amendment' or 'notices'
                    );
                    
                    const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                    const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                    
                    // Determine status based on workflow progression
                    let status = amendment.status;
                    if (finalCopy && finalCopy.user_received_at) {
                        status = 'completed';
                    } else if (finalCopy) {
                        status = 'final_uploaded';
                    } else if (draftCopy && draftCopy.status === 'draft_approved') {
                        status = 'draft_approved';
                    } else if (draftCopy) {
                        status = 'draft_uploaded';
                    } else {
                        status = 'pending';
                    }
                    
                    return {
                        ...amendment,
                        draft_copy: draftCopy ? draftCopy.filename : null,
                        draft_copy_id: draftCopy ? draftCopy.id : null,
                        final_copy: finalCopy ? finalCopy.filename : null,
                        final_copy_id: finalCopy ? finalCopy.id : null,
                        status: status
                    };
                });
                
                setData(processedData);
                setTaxReturnStatus(taxReturnsData);
            } catch (error) {
                console.error('Failed to fetch amendments:', error);
                setData([]);
            }
            setLoading(false);
        }
        fetchData();
    }, [selectedStatus, selectedYear]);

    // Get unique users from data
    const uniqueUsers = useMemo(() => {
        const users = {};
        data.forEach(item => {
            if (!users[item.user]) {
                users[item.user] = {
                    user_id: item.user,
                    user_name: item.user_name,
                    user_email: item.user_email,
                    years: [],
                    totalFiles: 0,
                    status: item.status
                };
            }
            if (item.year && !users[item.user].years.includes(item.year)) {
                users[item.user].years.push(item.year);
            }
            users[item.user].totalFiles++;
        });
        return Object.values(users).map(user => ({
            ...user,
            years: user.years.sort((a, b) => b - a)
        }));
    }, [data]);
    
    // Get unique years from data - include all years (even if already filed) for amendments
    const uniqueYears = useMemo(() => {
        const years = new Set();
        // Get years from amendment data
        data.forEach(item => {
            if (item.year) {
                years.add(item.year);
            }
        });
        // Also include years from uniqueUsers to show years that might be already filed
        uniqueUsers.forEach(user => {
            if (user.years && Array.isArray(user.years)) {
                user.years.forEach(year => {
                    years.add(year);
                });
            }
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [data, uniqueUsers]);
    
    // Filtered users based on search
    const filteredUsers = useMemo(() => {
        if (!search) return uniqueUsers;
        const searchLower = search.toLowerCase();
        return uniqueUsers.filter(user =>
            user.user_name.toLowerCase().includes(searchLower) ||
            user.user_email.toLowerCase().includes(searchLower)
        );
    }, [uniqueUsers, search]);
    
    // Get data for selected user
    const selectedUserData = useMemo(() => {
        if (!selectedUserForDetail && !selectedUserName) return [];
        const userId = selectedUserForDetail?.user_id || uniqueUsers.find(u => u.user_name === selectedUserName)?.user_id;
        if (!userId) return [];
        
        let userData = data.filter(item => item.user === userId);
        
        if (selectedYearForUser !== "All Years") {
            userData = userData.filter(item => item.year === parseInt(selectedYearForUser));
        }
        
        return userData;
    }, [data, selectedUserForDetail, selectedUserName, selectedYearForUser, uniqueUsers]);
    
    // Group selected user data by year
    const selectedUserGroupedData = useMemo(() => {
        const grouped = {};
        selectedUserData.forEach(item => {
            if (!grouped[item.year]) {
                grouped[item.year] = {
                    year: item.year,
                    files: [],
                    status: item.status,
                    draft_copy: item.draft_copy,
                    draft_copy_id: item.draft_copy_id,
                    final_copy: item.final_copy,
                    final_copy_id: item.final_copy_id
                };
            }
            grouped[item.year].files.push(item);
        });
        
        return Object.values(grouped).sort((a, b) => b.year - a.year);
    }, [selectedUserData]);

    // Filtered data
    const filteredData = useMemo(() => {
        let filtered = [...data];
        
        // Search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(item =>
                (item.user_name && item.user_name.toLowerCase().includes(searchLower)) ||
                (item.user_email && item.user_email.toLowerCase().includes(searchLower)) ||
                (item.original_filename && item.original_filename.toLowerCase().includes(searchLower)) ||
                (item.title && item.title.toLowerCase().includes(searchLower))
            );
        }
        
        // Year filter (already applied in API call, but keep for client-side filtering if needed)
        if (selectedYear !== "All Years") {
            filtered = filtered.filter(item => item.year === parseInt(selectedYear));
        }
        
        // Status filter (already applied in API call, but keep for client-side filtering if needed)
        if (selectedStatus !== "All Statuses") {
            filtered = filtered.filter(item => item.status === selectedStatus);
        }
        
        return filtered;
    }, [data, search, selectedYear, selectedStatus]);

    // Paginated data
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * perPage;
        return filteredData.slice(start, start + perPage);
    }, [filteredData, currentPage, perPage]);

    // Columns definition
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "60px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
            sortable: false,
            center: true,
        },
        {
            name: "User Name",
            selector: row => row.user_name,
            sortable: true,
            minWidth: "130px",
            style: { fontWeight: 500, whiteSpace: "nowrap" },
            cell: row => <span>{row.user_name || 'N/A'}</span>
        },
        {
            name: "File Name",
            selector: row => row.original_filename,
            sortable: true,
            minWidth: "200px",
            style: { 
                fontSize: "12px",
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            cell: row => (
                <span 
                    style={{ 
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%"
                    }}
                    title={row.original_filename || 'N/A'}
                >
                    {row.original_filename || 'N/A'}
                </span>
            )
        },
        {
            name: "Category",
            selector: () => "Amendment",
            sortable: false,
            minWidth: "120px",
            style: { color: "#217eed", fontWeight: 500, whiteSpace: "nowrap" },
            cell: () => <span>Amendment</span>
        },
        {
            name: "Tax Year",
            selector: row => row.year,
            sortable: true,
            width: "90px",
            style: { 
                fontWeight: 600, 
                color: "#112", 
                textAlign: "center",
                fontSize: "12px"
            },
            cell: row => <span>{row.year || 'N/A'}</span>
        },
        {
            name: "Status",
            selector: row => row.status,
            sortable: true,
            minWidth: "100px",
            style: { textAlign: "center", whiteSpace: "nowrap" },
            cell: row => (
                <span style={{
                    borderRadius: "12px",
                    padding: "4px 12px",
                    fontWeight: 600,
                    color: "#fff",
                    fontSize: 12,
                    background: row.status === "completed" ? "#21bb55" :
                               row.status === "final_uploaded" ? "#f59e0b" :
                               row.status === "draft_approved" ? "#3b82f6" :
                               row.status === "draft_uploaded" ? "#6366f1" :
                               row.status === "pending" ? "#f59e0b" :
                               row.status === "approved" ? "#21bb55" : 
                               row.status === "rejected" ? "#dc2626" : 
                               row.status === "processing" ? "#f59e0b" : "#6b7280",
                    display: "inline-block",
                    minWidth: 60
                }}>
                    {row.status === "completed" ? "Completed" :
                     row.status === "final_uploaded" ? "Final Copy Sent" :
                     row.status === "draft_approved" ? "Draft Approved" :
                     row.status === "draft_uploaded" ? "Draft Uploaded" :
                     row.status === "pending" ? "Pending" :
                     row.status === "approved" ? "Approved" :
                     row.status === "rejected" ? "Rejected" :
                     row.status === "processing" ? "Processing" :
                     (row.status || 'N/A')}
                </span>
            ),
            center: true
        },
        {
            name: "Draft Copy",
            selector: row => row.draft_copy,
            sortable: false,
            width: "120px",
            style: { textAlign: "center" },
            cell: row => {
                if (row.draft_copy) {
                    return (
                        <button
                            onClick={() => handleDownloadDraft(row)}
                            style={{
                                background: "#3b82f6",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                fontSize: "11px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}
                            title="Download Draft Copy"
                        >
                            <AiOutlineDownload style={{ fontSize: "12px" }} />
                            Download
                        </button>
                    );
                }
                return <span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>;
            }
        },
        {
            name: "Final Copy",
            selector: row => row.final_copy,
            sortable: false,
            width: "120px",
            style: { textAlign: "center" },
            cell: row => {
                if (row.final_copy) {
                    return (
                        <button
                            onClick={() => handleDownloadFinal(row)}
                            style={{
                                background: "#10b981",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                fontSize: "11px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}
                            title="Download Final Copy"
                        >
                            <AiOutlineDownload style={{ fontSize: "12px" }} />
                            Download
                        </button>
                    );
                }
                return <span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>;
            }
        },
        {
            name: "Upload Date (CST)",
            selector: row => row.uploaded_at,
            sortable: true,
            minWidth: "160px",
            style: { 
                color: "#555", 
                fontSize: "11px",
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            cell: row => (
                <span 
                    style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%"
                    }}
                    title={row.uploaded_at ? formatToCST(row.uploaded_at) : 'N/A'}
                >
                    {row.uploaded_at ? formatToCST(row.uploaded_at) : 'N/A'}
                </span>
            )
        },
        {
            name: "Actions",
            cell: row => (
                <button
                    onClick={() => handleDownload(row)}
                    style={{
                        background: "#4338ca",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "11px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                    }}
                    title="Download Amendment"
                >
                    <AiOutlineDownload style={{ fontSize: "12px" }} />
                    Download
                </button>
            ),
            ignoreRowClick: true,
            width: "120px"
        },
    ], [currentPage, perPage]);

    async function handleDownload(row) {
        setLoading(true);
        try {
            await apiDownloadAmendment({ id: row.id, filename: row.original_filename });
            setSuccessMessage(`Amendment "${row.original_filename}" downloaded successfully`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download amendment. Please try again.');
        }
        setLoading(false);
    }

    function handlePageChange(page) {
        setCurrentPage(page);
    }

    function handlePerRowsChange(newPerPage, page) {
        setPerPage(newPerPage);
        setCurrentPage(page);
    }

    function resetFilters() {
        setSearch("");
        setSelectedYear("All Years");
        setSelectedStatus("All Statuses");
        setCurrentPage(1);
    }
    
    // Workflow management functions
    function openDraftModal(userData) {
        setSelectedUser(userData);
        setShowDraftModal(true);
        setDraftFile(null);
    }

    function openFinalModal(userData) {
        setSelectedUser(userData);
        setShowFinalModal(true);
        setFinalFile(null);
    }

    function closeDraftModal() {
        setShowDraftModal(false);
        setSelectedUser(null);
        setDraftFile(null);
    }

    function closeFinalModal() {
        setShowFinalModal(false);
        setSelectedUser(null);
        setFinalFile(null);
    }

    function handleDraftFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            setDraftFile(file);
        }
    }

    function handleFinalFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            setFinalFile(file);
        }
    }

    async function handleDraftUpload() {
        if (!draftFile || !selectedUser) return;

        setUploadLoading(true);
        try {
            // Get amendment_id from the first amendment file for this user/year
            // All amendments for a user/year should have the same category
            const amendmentId = selectedUser.amendment_id || null;
            await apiUploadDraftCopy(selectedUser.user_id, selectedUser.year_display, draftFile, amendmentId);
            setSuccessMessage(`Draft copy uploaded successfully for ${selectedUser.user_name} (${selectedUser.year_display})`);
            setTimeout(() => setSuccessMessage(""), 5000);
            
            // Refresh data
            const [amendmentsData, taxReturnsData] = await Promise.all([
                apiGetAmendments(selectedStatus, selectedYear),
                apiGetAllTaxReturnStatuses()
            ]);
            
            // Process data
            const processedData = amendmentsData.map(amendment => {
                const userTaxReturns = taxReturnsData.filter(tr => 
                    tr.user === amendment.user && tr.year === amendment.year
                );
                const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                
                let status = amendment.status;
                if (finalCopy && finalCopy.user_received_at) {
                    status = 'completed';
                } else if (finalCopy) {
                    status = 'final_uploaded';
                } else if (draftCopy && draftCopy.is_submitted) {
                    status = 'draft_approved';
                } else if (draftCopy) {
                    status = 'draft_uploaded';
                } else {
                    status = 'pending';
                }
                
                return {
                    ...amendment,
                    draft_copy: draftCopy ? draftCopy.filename : null,
                    draft_copy_id: draftCopy ? draftCopy.id : null,
                    final_copy: finalCopy ? finalCopy.filename : null,
                    final_copy_id: finalCopy ? finalCopy.id : null,
                    status: status
                };
            });
            
            setData(processedData);
            setTaxReturnStatus(taxReturnsData);
            
            closeDraftModal();
        } catch (error) {
            console.error('Draft upload failed:', error);
            alert('Failed to upload draft copy. Please try again.');
        }
        setUploadLoading(false);
    }

    async function handleFinalUpload() {
        if (!finalFile || !selectedUser) return;

        setUploadLoading(true);
        try {
            // Get amendment_id from the first amendment file for this user/year
            // All amendments for a user/year should have the same category
            const amendmentId = selectedUser.amendment_id || null;
            await apiUploadFinalCopy(selectedUser.user_id, selectedUser.year_display, finalFile, amendmentId);
            setSuccessMessage(`Final copy uploaded successfully for ${selectedUser.user_name} (${selectedUser.year_display})`);
            setTimeout(() => setSuccessMessage(""), 5000);
            
            // Refresh data
            const [amendmentsData, taxReturnsData] = await Promise.all([
                apiGetAmendments(selectedStatus, selectedYear),
                apiGetAllTaxReturnStatuses()
            ]);
            
            // Process data
            const processedData = amendmentsData.map(amendment => {
                const userTaxReturns = taxReturnsData.filter(tr => 
                    tr.user === amendment.user && tr.year === amendment.year
                );
                const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                
                let status = amendment.status;
                if (finalCopy && finalCopy.user_received_at) {
                    status = 'completed';
                } else if (finalCopy) {
                    status = 'final_uploaded';
                } else if (draftCopy && draftCopy.is_submitted) {
                    status = 'draft_approved';
                } else if (draftCopy) {
                    status = 'draft_uploaded';
                } else {
                    status = 'pending';
                }
                
                return {
                    ...amendment,
                    draft_copy: draftCopy ? draftCopy.filename : null,
                    draft_copy_id: draftCopy ? draftCopy.id : null,
                    final_copy: finalCopy ? finalCopy.filename : null,
                    final_copy_id: finalCopy ? finalCopy.id : null,
                    status: status
                };
            });
            
            setData(processedData);
            setTaxReturnStatus(taxReturnsData);
            
            closeFinalModal();
        } catch (error) {
            console.error('Final upload failed:', error);
            alert('Failed to upload final copy. Please try again.');
        }
        setUploadLoading(false);
    }
    
    // View switching functions
    function switchToUserDetail(user) {
        setSelectedUserForDetail(user);
        setSelectedUserName(user.user_name);
        setSelectedYearForUser("All Years");
        setCurrentYearIndex(0);
        setViewMode('user-detail');
    }

    function switchToOverview() {
        setSelectedUserForDetail(null);
        setSelectedUserName("");
        setSelectedYearForUser("All Years");
        setCurrentYearIndex(0);
        setViewMode('overview');
    }
    
    function handleYearSelection(e) {
        setSelectedYearForUser(e.target.value);
        setCurrentYearIndex(0);
    }
    
    // Year navigation functions
    function goToPreviousYear() {
        if (currentYearIndex > 0) {
            setCurrentYearIndex(currentYearIndex - 1);
        }
    }

    function goToNextYear() {
        if (currentYearIndex < selectedUserGroupedData.length - 1) {
            setCurrentYearIndex(currentYearIndex + 1);
        }
    }

    async function handleDownloadDraft(row) {
        try {
            await apiDownloadDraftCopy(row.draft_copy_id);
        } catch (error) {
            console.error('Draft download failed:', error);
            alert('Failed to download draft copy. Please try again.');
        }
    }

    async function handleDownloadFinal(row) {
        try {
            await apiDownloadFinalCopy(row.final_copy_id);
        } catch (error) {
            console.error('Final download failed:', error);
            alert('Failed to download final copy. Please try again.');
        }
    }

    const customStyles = {
        rows: { style: { fontSize: 12, background: "#f8fafc", whiteSpace: "nowrap" } },
        table: { style: { width: "100%", minWidth: 800, background: "#fff", borderRadius: 20 } },
        headRow: { style: { fontWeight: 700, fontSize: 13, background: "#eceffd", color: "#4338ca" } },
        headCells: { style: { borderBottom: "1.5px solid #a5b4fc", padding: "6px 12px" } },
        cells: { style: { padding: "6px 12px" } },
        pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } },
    };

    return (
        <section style={{
            userSelect: "none",
            width: "100%",
            padding: 16,
        }}>
            {/* Success Message */}
            {successMessage && (
                <div style={{
                    background: "#dcfce7",
                    border: "1px solid #16a34a",
                    color: "#166534",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    position: "relative",
                    zIndex: 1000
                }}>
                    <AiOutlineCheckCircle style={{ fontSize: "18px" }} />
                    {successMessage}
                </div>
            )}

            {/* Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20
            }}>
                <h1 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#4338ca",
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 10
                }}>
                    <AiOutlineFileAdd size={32} />
                    Amendment Uploads
                </h1>
            </div>

            {/* Filters */}
            <div style={{
                display: "flex",
                gap: 12,
                marginBottom: 20,
                flexWrap: "wrap",
                alignItems: "center"
            }}>
                <div style={{ position: "relative", flex: 1, minWidth: 250 }}>
                    <AiOutlineSearch style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9ca3af",
                        fontSize: 18
                    }} />
                    <input
                        type="text"
                        placeholder="Search by user name, email, or filename..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: "100%",
                            paddingLeft: 40,
                            paddingRight: 12,
                            paddingTop: 10,
                            paddingBottom: 10,
                            borderRadius: 8,
                            border: "1px solid #ced4da",
                            fontSize: 14,
                            outline: "none"
                        }}
                    />
                </div>

                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #ced4da",
                        fontSize: 14,
                        minWidth: 150,
                        cursor: "pointer"
                    }}
                >
                    <option value="All Years">All Years</option>
                    {uniqueYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>

                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #ced4da",
                        fontSize: 14,
                        minWidth: 150,
                        cursor: "pointer"
                    }}
                >
                    <option value="All Statuses">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="draft_uploaded">Draft Uploaded</option>
                    <option value="draft_approved">Draft Approved</option>
                    <option value="final_uploaded">Final Copy Sent</option>
                    <option value="completed">Completed</option>
                </select>

                {(search || selectedYear !== "All Years" || selectedStatus !== "All Statuses") && (
                    <button
                        onClick={resetFilters}
                        style={{
                            padding: "10px 16px",
                            borderRadius: 8,
                            border: "1px solid #ced4da",
                            background: "#fff",
                            color: "#4338ca",
                            fontSize: 14,
                            cursor: "pointer",
                            fontWeight: 500
                        }}
                    >
                        Reset Filters
                    </button>
                )}
            </div>

            {/* Workflow Management Section */}
            {viewMode === 'overview' ? (
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "16px"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            <AiOutlineFolder style={{ fontSize: "20px", color: "#4338ca" }} />
                            <h3 style={{
                                margin: 0,
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#4338ca"
                            }}>
                                Amendment Workflow Management
                            </h3>
                        </div>
                        <div style={{
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            {uniqueUsers.length} user{uniqueUsers.length !== 1 ? 's' : ''} with amendments
                        </div>
                    </div>

                    {/* Show empty state if no users */}
                    {uniqueUsers.length === 0 ? (
                        <div style={{
                            textAlign: "center",
                            padding: "40px 20px",
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px"
                        }}>
                            <AiOutlineFileText style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }} />
                            <h4 style={{
                                margin: "0 0 8px 0",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#374151"
                            }}>
                                No Amendment Uploads Found
                            </h4>
                            <p style={{
                                margin: "0",
                                fontSize: "14px",
                                color: "#6b7280"
                            }}>
                                No users have uploaded amendment documents yet.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* User Selection Grid */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: "16px",
                                marginBottom: "20px"
                            }}>
                                {filteredUsers.map((user, index) => {
                                    // Get status for this user (use the most recent year's status)
                                    const userAmendments = data.filter(item => item.user === user.user_id);
                                    const latestStatus = userAmendments.length > 0 ? userAmendments[0].status : 'pending';
                                    
                                    return (
                                        <div key={index} style={{
                                            background: "#fff",
                                            border: "2px solid #e2e8f0",
                                            borderRadius: "12px",
                                            padding: "20px",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                            cursor: "pointer",
                                            transition: "all 0.3s ease",
                                            position: "relative",
                                            overflow: "hidden"
                                        }}
                                            onClick={() => switchToUserDetail(user)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = "0 8px 16px rgba(67, 56, 202, 0.15)";
                                                e.currentTarget.style.transform = "translateY(-4px)";
                                                e.currentTarget.style.borderColor = "#4338ca";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.borderColor = "#e2e8f0";
                                            }}
                                        >
                                            {/* Status Badge */}
                                            <div style={{
                                                position: "absolute",
                                                top: 12,
                                                right: 12,
                                                padding: "4px 12px",
                                                borderRadius: "20px",
                                                fontSize: "11px",
                                                fontWeight: "600",
                                                background: latestStatus === "completed" ? "#dcfce7" :
                                                           latestStatus === "final_uploaded" ? "#fef3c7" :
                                                           latestStatus === "draft_approved" ? "#dbeafe" :
                                                           latestStatus === "draft_uploaded" ? "#e0e7ff" :
                                                           "#f3f4f6",
                                                color: latestStatus === "completed" ? "#166534" :
                                                       latestStatus === "final_uploaded" ? "#92400e" :
                                                       latestStatus === "draft_approved" ? "#1e40af" :
                                                       latestStatus === "draft_uploaded" ? "#3730a3" :
                                                       "#6b7280"
                                            }}>
                                                {latestStatus === "completed" ? "✓ Completed" :
                                                 latestStatus === "final_uploaded" ? "📤 Final Sent" :
                                                 latestStatus === "draft_approved" ? "✓ Draft Approved" :
                                                 latestStatus === "draft_uploaded" ? "📄 Draft Uploaded" :
                                                 "⏳ Pending"}
                                            </div>
                                            
                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                                marginBottom: "12px"
                                            }}>
                                                <div style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#fff",
                                                    fontWeight: "600",
                                                    fontSize: "16px"
                                                }}>
                                                    {user.user_name.charAt(0).toUpperCase()}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontWeight: "600",
                                                        fontSize: "16px",
                                                        color: "#374151",
                                                        marginBottom: "4px"
                                                    }}>
                                                        {user.user_name}
                                                    </div>
                                                    <div style={{
                                                        fontSize: "13px",
                                                        color: "#6b7280"
                                                    }}>
                                                        {user.user_email}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                marginBottom: "8px",
                                                padding: "8px",
                                                background: "#f9fafb",
                                                borderRadius: "6px"
                                            }}>
                                                <AiOutlineFileText style={{ fontSize: "16px", color: "#4338ca" }} />
                                                <span style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "500"
                                                }}>
                                                    {user.totalFiles} amendment{user.totalFiles !== 1 ? 's' : ''}
                                                </span>
                                            </div>

                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: "8px",
                                                background: "#f9fafb",
                                                borderRadius: "6px",
                                                marginBottom: "12px"
                                            }}>
                                                <AiOutlineCalendar style={{ fontSize: "16px", color: "#4338ca" }} />
                                                <span style={{
                                                    fontSize: "14px",
                                                    color: "#374151"
                                                }}>
                                                    Year{user.years.length > 1 ? 's' : ''}: {user.years.join(", ")}
                                                </span>
                                            </div>

                                            <div style={{
                                                marginTop: "12px",
                                                padding: "10px",
                                                background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)",
                                                color: "#fff",
                                                borderRadius: "8px",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                textAlign: "center",
                                                cursor: "pointer"
                                            }}>
                                                Manage Workflow →
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px"
                }}>
                    {/* Back Button */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "20px"
                    }}>
                        <button
                            onClick={switchToOverview}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 16px",
                                background: "#fff",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                color: "#374151",
                                fontSize: "14px",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = "#f3f4f6";
                                e.target.style.borderColor = "#4338ca";
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = "#fff";
                                e.target.style.borderColor = "#d1d5db";
                            }}
                        >
                            <AiOutlineLeft style={{ fontSize: "16px" }} />
                            Back to Overview
                        </button>
                        <div style={{
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            {selectedUserData.length} amendment{selectedUserData.length !== 1 ? 's' : ''}
                        </div>
                    </div>

                    {/* User Info */}
                    {(selectedUserForDetail || selectedUserName) && (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px",
                            padding: "20px",
                            marginBottom: "20px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                marginBottom: "16px"
                            }}>
                                <div style={{
                                    width: "50px",
                                    height: "50px",
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, #4338ca 0%, #6366f1 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontWeight: "600",
                                    fontSize: "20px"
                                }}>
                                    {(selectedUserForDetail?.user_name || selectedUserName).charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: "20px",
                                        fontWeight: "600",
                                        color: "#374151"
                                    }}>
                                        {selectedUserForDetail ? selectedUserForDetail.user_name : selectedUserName}
                                    </h3>
                                    <p style={{
                                        margin: "4px 0 0 0",
                                        fontSize: "14px",
                                        color: "#6b7280"
                                    }}>
                                        {selectedUserForDetail ? selectedUserForDetail.user_email : uniqueUsers.find(u => u.user_name === selectedUserName)?.user_email}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Year Navigation and Workflow Card */}
                    {selectedUserGroupedData.length > 0 ? (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px",
                            padding: "24px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                        }}>
                            {/* Year Navigation Header */}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "24px",
                                paddingBottom: "16px",
                                borderBottom: "2px solid #f3f4f6"
                            }}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px"
                                }}>
                                    <button
                                        onClick={goToPreviousYear}
                                        disabled={currentYearIndex === 0}
                                        style={{
                                            background: currentYearIndex === 0 ? "#f3f4f6" : "#4338ca",
                                            color: currentYearIndex === 0 ? "#9ca3af" : "#fff",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "40px",
                                            height: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: currentYearIndex === 0 ? "not-allowed" : "pointer",
                                            fontSize: "18px",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <AiOutlineLeft />
                                    </button>
                                    
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                    }}>
                                        <AiOutlineCalendar style={{ fontSize: "20px", color: "#4338ca" }} />
                                        <span style={{
                                            fontWeight: "700",
                                            fontSize: "20px",
                                            color: "#374151"
                                        }}>
                                            Tax Year {selectedUserGroupedData[currentYearIndex]?.year}
                                        </span>
                                    </div>
                                    
                                    <button
                                        onClick={goToNextYear}
                                        disabled={currentYearIndex === selectedUserGroupedData.length - 1}
                                        style={{
                                            background: currentYearIndex === selectedUserGroupedData.length - 1 ? "#f3f4f6" : "#4338ca",
                                            color: currentYearIndex === selectedUserGroupedData.length - 1 ? "#9ca3af" : "#fff",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "40px",
                                            height: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: currentYearIndex === selectedUserGroupedData.length - 1 ? "not-allowed" : "pointer",
                                            fontSize: "18px",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <AiOutlineRight />
                                    </button>
                                </div>

                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px"
                                }}>
                                    <span style={{
                                        fontSize: "14px",
                                        color: "#6b7280"
                                    }}>
                                        {currentYearIndex + 1} of {selectedUserGroupedData.length}
                                    </span>
                                    <div style={{
                                        padding: "8px 16px",
                                        borderRadius: "20px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        background: selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? "#dcfce7" :
                                                    selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? "#fef3c7" :
                                                    selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? "#dbeafe" :
                                                    selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? "#e0e7ff" :
                                                    "#f3f4f6",
                                        color: selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? "#166534" :
                                               selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? "#92400e" :
                                               selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? "#1e40af" :
                                               selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? "#3730a3" :
                                               "#6b7280"
                                    }}>
                                        {selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? '✓ Completed' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? '📤 Final Copy Sent' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? '✓ Draft Approved' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? '📄 Draft Uploaded' :
                                         '⏳ Pending'}
                                    </div>
                                </div>
                            </div>

                            {/* Workflow Steps Visualization */}
                            <div style={{
                                marginBottom: "24px",
                                padding: "20px",
                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                borderRadius: "12px",
                                border: "1px solid #e2e8f0"
                            }}>
                                <h4 style={{
                                    margin: "0 0 16px 0",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#374151"
                                }}>
                                    Workflow Progress
                                </h4>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    position: "relative"
                                }}>
                                    {/* Workflow Steps */}
                                    {[
                                        { step: 1, label: "Uploaded", status: selectedUserGroupedData[currentYearIndex]?.status !== 'pending' },
                                        { step: 2, label: "Draft Sent", status: ['draft_uploaded', 'draft_approved', 'final_uploaded', 'completed'].includes(selectedUserGroupedData[currentYearIndex]?.status) },
                                        { step: 3, label: "Draft Approved", status: ['draft_approved', 'final_uploaded', 'completed'].includes(selectedUserGroupedData[currentYearIndex]?.status) },
                                        { step: 4, label: "Final Sent", status: ['final_uploaded', 'completed'].includes(selectedUserGroupedData[currentYearIndex]?.status) },
                                        { step: 5, label: "Completed", status: selectedUserGroupedData[currentYearIndex]?.status === 'completed' }
                                    ].map((stepItem, idx) => (
                                        <div key={idx} style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            flex: 1,
                                            position: "relative"
                                        }}>
                                            <div style={{
                                                width: "40px",
                                                height: "40px",
                                                borderRadius: "50%",
                                                background: stepItem.status ? "#10b981" : "#e5e7eb",
                                                color: stepItem.status ? "#fff" : "#9ca3af",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: "600",
                                                fontSize: "14px",
                                                marginBottom: "8px",
                                                border: stepItem.status ? "3px solid #10b981" : "3px solid #e5e7eb",
                                                boxShadow: stepItem.status ? "0 0 0 4px rgba(16, 185, 129, 0.1)" : "none",
                                                transition: "all 0.3s"
                                            }}>
                                                {stepItem.status ? "✓" : stepItem.step}
                                            </div>
                                            <span style={{
                                                fontSize: "12px",
                                                color: stepItem.status ? "#10b981" : "#9ca3af",
                                                fontWeight: stepItem.status ? "600" : "500",
                                                textAlign: "center"
                                            }}>
                                                {stepItem.label}
                                            </span>
                                            {idx < 4 && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: "20px",
                                                    left: "calc(50% + 20px)",
                                                    width: "calc(100% - 40px)",
                                                    height: "3px",
                                                    background: stepItem.status ? "#10b981" : "#e5e7eb",
                                                    zIndex: 0
                                                }} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Current Year Content */}
                            {selectedUserGroupedData[currentYearIndex] && (
                                <div>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "20px",
                                        padding: "12px",
                                        background: "#f9fafb",
                                        borderRadius: "8px"
                                    }}>
                                        <AiOutlineFileText style={{ fontSize: "18px", color: "#4338ca" }} />
                                        <span style={{
                                            fontSize: "15px",
                                            color: "#374151",
                                            fontWeight: "500"
                                        }}>
                                            {selectedUserGroupedData[currentYearIndex].files.length} amendment file{selectedUserGroupedData[currentYearIndex].files.length !== 1 ? 's' : ''} uploaded
                                        </span>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{
                                        display: "flex",
                                        gap: "12px",
                                        flexWrap: "wrap",
                                        marginBottom: "20px"
                                    }}>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name === selectedUserName);
                                                    const year = selectedUserGroupedData[currentYearIndex].year;
                                                    
                                                    if (!currentUser) {
                                                        alert('User not found');
                                                        return;
                                                    }
                                                    
                                                    setLoading(true);
                                                    await apiDownloadAmendmentsZip(currentUser.user_id, year);
                                                    setSuccessMessage(`All amendment files for ${year} downloaded successfully`);
                                                    setTimeout(() => setSuccessMessage(""), 3000);
                                                } catch (error) {
                                                    console.error('Download failed:', error);
                                                    alert('Failed to download files: ' + error.message);
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            style={{
                                                background: "#059669",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "12px 20px",
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                transition: "all 0.2s",
                                                boxShadow: "0 2px 4px rgba(5, 150, 105, 0.2)"
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = "#047857";
                                                e.target.style.transform = "translateY(-2px)";
                                                e.target.style.boxShadow = "0 4px 8px rgba(5, 150, 105, 0.3)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = "#059669";
                                                e.target.style.transform = "translateY(0)";
                                                e.target.style.boxShadow = "0 2px 4px rgba(5, 150, 105, 0.2)";
                                            }}
                                        >
                                            <AiOutlineDownload style={{ fontSize: "16px" }} />
                                            Download All Files
                                        </button>
                                        
                                        <button
                                            onClick={() => {
                                                const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name === selectedUserName);
                                                const currentYearData = selectedUserGroupedData[currentYearIndex];
                                                // Get the first amendment ID for this user/year (all should have same category)
                                                const firstAmendmentId = currentYearData.files && currentYearData.files.length > 0 
                                                    ? currentYearData.files[0].id 
                                                    : null;
                                                openDraftModal({
                                                    user_id: currentUser.user_id,
                                                    user_name: currentUser.user_name,
                                                    year_display: currentYearData.year,
                                                    amendment_id: firstAmendmentId
                                                });
                                            }}
                                            disabled={selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                     selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || 
                                                     selectedUserGroupedData[currentYearIndex].status === 'completed'}
                                            style={{
                                                background: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                            selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || 
                                                            selectedUserGroupedData[currentYearIndex].status === 'completed') ? "#9ca3af" : "#3b82f6",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "12px 20px",
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                cursor: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                         selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || 
                                                         selectedUserGroupedData[currentYearIndex].status === 'completed') ? "not-allowed" : "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                transition: "all 0.2s",
                                                boxShadow: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                           selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || 
                                                           selectedUserGroupedData[currentYearIndex].status === 'completed') ? "none" : "0 2px 4px rgba(59, 130, 246, 0.2)"
                                            }}
                                            title={selectedUserGroupedData[currentYearIndex].status === 'draft_approved' ? "Draft already approved. Upload Final instead." : 
                                                   selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' ? "Final copy already sent. Waiting for user acknowledgment." :
                                                   selectedUserGroupedData[currentYearIndex].status === 'completed' ? "Workflow completed." :
                                                   "Upload Draft Copy"}
                                        >
                                            <AiOutlineFileText style={{ fontSize: "16px" }} />
                                            Upload Draft Copy
                                        </button>
                                        
                                        <button
                                            onClick={() => {
                                                const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name === selectedUserName);
                                                const currentYearData = selectedUserGroupedData[currentYearIndex];
                                                // Get the first amendment ID for this user/year (all should have same category)
                                                const firstAmendmentId = currentYearData.files && currentYearData.files.length > 0 
                                                    ? currentYearData.files[0].id 
                                                    : null;
                                                openFinalModal({
                                                    user_id: currentUser.user_id,
                                                    user_name: currentUser.user_name,
                                                    year_display: currentYearData.year,
                                                    amendment_id: firstAmendmentId
                                                });
                                            }}
                                            disabled={selectedUserGroupedData[currentYearIndex].status === 'completed' || 
                                                     (selectedUserGroupedData[currentYearIndex].status !== 'draft_approved' && 
                                                      selectedUserGroupedData[currentYearIndex].status !== 'final_uploaded')}
                                            style={{
                                                background: selectedUserGroupedData[currentYearIndex].status === 'completed' ? "#9ca3af" :
                                                           (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                            selectedUserGroupedData[currentYearIndex].status === 'final_uploaded') ? "#10b981" : "#9ca3af",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "12px 20px",
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                cursor: selectedUserGroupedData[currentYearIndex].status === 'completed' ? "not-allowed" :
                                                       (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                        selectedUserGroupedData[currentYearIndex].status === 'final_uploaded') ? "pointer" : "not-allowed",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                transition: "all 0.2s",
                                                boxShadow: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || 
                                                           selectedUserGroupedData[currentYearIndex].status === 'final_uploaded') ? "0 2px 4px rgba(16, 185, 129, 0.2)" : "none"
                                            }}
                                            title={selectedUserGroupedData[currentYearIndex].status === 'completed' ? "Workflow completed." :
                                                   selectedUserGroupedData[currentYearIndex].status === 'draft_approved' ? "Upload Final Copy (Draft Approved)" : 
                                                   selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' ? "Final copy already uploaded" : 
                                                   "Draft must be approved first"}
                                        >
                                            <AiOutlineCheckCircle style={{ fontSize: "16px" }} />
                                            Upload Final Copy
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (selectedUserName || selectedUserForDetail) && selectedUserGroupedData.length === 0 ? (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px",
                            padding: "40px 20px",
                            textAlign: "center"
                        }}>
                            <AiOutlineFileText style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }} />
                            <h4 style={{
                                margin: "0 0 8px 0",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#374151"
                            }}>
                                No Amendments for This User
                            </h4>
                            <p style={{
                                margin: "0",
                                fontSize: "14px",
                                color: "#6b7280"
                            }}>
                                {selectedUserForDetail ? selectedUserForDetail.user_name : selectedUserName} has not uploaded any amendment documents yet.
                            </p>
                        </div>
                    ) : null}
                </div>
            )}

            {/* DataTable - Only show in overview mode */}
            {viewMode === 'overview' && (
                <DataTable
                    columns={columns}
                    data={paginatedData}
                    highlightOnHover
                    striped
                    pagination
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    paginationDefaultPage={currentPage}
                    paginationTotalRows={filteredData.length}
                    progressPending={loading}
                    noHeader
                    responsive
                    persistTableHead
                    customStyles={customStyles}
                    noDataComponent={
                        <div style={{
                            textAlign: "center",
                            padding: "40px 20px",
                            color: "#6b7280"
                        }}>
                            {loading ? "Loading..." : "No amendments found"}
                        </div>
                    }
                />
            )}
            
            {/* Draft Upload Modal */}
            {showDraftModal && selectedUser && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "24px",
                        maxWidth: "500px",
                        width: "90%",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{
                            margin: "0 0 16px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151"
                        }}>
                            Upload Draft Copy
                        </h3>
                        <div style={{ marginBottom: "16px" }}>
                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>User:</strong> {selectedUser.user_name}
                            </p>
                            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>Year:</strong> {selectedUser.year_display}
                            </p>
                        </div>
                        <div style={{ marginBottom: "20px" }}>
                            <label style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#374151"
                            }}>
                                Select Draft File
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleDraftFileChange}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "14px"
                                }}
                            />
                            {draftFile && (
                                <p style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "13px",
                                    color: "#059669"
                                }}>
                                    Selected: {draftFile.name}
                                </p>
                            )}
                        </div>
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end"
                        }}>
                            <button
                                onClick={closeDraftModal}
                                style={{
                                    padding: "8px 16px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDraftUpload}
                                disabled={!draftFile || uploadLoading}
                                style={{
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: !draftFile || uploadLoading ? "#9ca3af" : "#3b82f6",
                                    color: "#fff",
                                    fontSize: "14px",
                                    cursor: !draftFile || uploadLoading ? "not-allowed" : "pointer"
                                }}
                            >
                                {uploadLoading ? "Uploading..." : "Upload Draft"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Final Upload Modal */}
            {showFinalModal && selectedUser && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "24px",
                        maxWidth: "500px",
                        width: "90%",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{
                            margin: "0 0 16px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151"
                        }}>
                            Upload Final Copy
                        </h3>
                        <div style={{ marginBottom: "16px" }}>
                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>User:</strong> {selectedUser.user_name}
                            </p>
                            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>Year:</strong> {selectedUser.year_display}
                            </p>
                        </div>
                        <div style={{ marginBottom: "20px" }}>
                            <label style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#374151"
                            }}>
                                Select Final File
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleFinalFileChange}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "14px"
                                }}
                            />
                            {finalFile && (
                                <p style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "13px",
                                    color: "#059669"
                                }}>
                                    Selected: {finalFile.name}
                                </p>
                            )}
                        </div>
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end"
                        }}>
                            <button
                                onClick={closeFinalModal}
                                style={{
                                    padding: "8px 16px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFinalUpload}
                                disabled={!finalFile || uploadLoading}
                                style={{
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: !finalFile || uploadLoading ? "#9ca3af" : "#10b981",
                                    color: "#fff",
                                    fontSize: "14px",
                                    cursor: !finalFile || uploadLoading ? "not-allowed" : "pointer"
                                }}
                            >
                                {uploadLoading ? "Uploading..." : "Upload Final"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

